import { defaultRampName } from '@/lib/cutFillConvention';
import { useTranslation } from 'react-i18next';
import { Loader2, RotateCw } from 'lucide-react';
import { CheckCircleSolid, ClockCircleSolid, DangerCircleSolid } from '@mynaui/icons-react';

import { useDialog } from '@/providers/DialogProvider';
import { Button } from '@/components/ui/button';
import { RunSurfaceDifferenceDialog } from '@/components/RunSurfaceDifferenceDialog';
import { formatDateTime } from '@/components/BatchList';
import { useFeatureCheck } from '@/hooks/useFeature';
import type { LayersQuery, SurfaceDifferenceJobsQuery } from '@/graphql/graphql';
import { PC_METHODS } from '@/components/SurfacePicker';

type Job = SurfaceDifferenceJobsQuery['surfaceDifferenceJobs'][number];
type LayerData = LayersQuery['layers'][number];

interface Props {
  job: Job;
  candidateLayers: LayerData[];
  projectId: string;
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'PROCESSING') return <Loader2 className='size-5 animate-spin text-blue-600' />;
  if (status === 'PROCESSED') return <CheckCircleSolid className='size-5 text-emerald-500' />;
  if (status === 'FAILED') return <DangerCircleSolid className='size-5 text-rose-500' />;
  return <ClockCircleSolid className='size-5 text-amber-500' />;
}

type PcMethod = (typeof PC_METHODS)[number];
const asPcMethod = (v: unknown): PcMethod =>
  PC_METHODS.includes(v as PcMethod) ? (v as PcMethod) : 'min';

const m3 = (v: unknown) =>
  typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(v >= 100 ? 1 : 3)} m³` : '—';

export function SurfaceDifferenceRow({ job, candidateLayers, projectId }: Props) {
  const { t } = useTranslation('project');
  const { openDialog } = useDialog();
  const featureCheck = useFeatureCheck();

  const input = (job.inputJson ?? {}) as Record<string, unknown>;
  const params = (input.params ?? {}) as Record<string, unknown>;
  const stats = ((job.outputJson as Record<string, unknown> | null)?.stats ?? {}) as Record<string, unknown>;

  const topLayerId = input.topLayerId as string | undefined;
  const bottomLayerId = input.bottomLayerId as string | undefined;
  const topLayerName = (input.topLayerName as string | undefined) ?? '—';
  const bottomLayerName = (input.bottomLayerName as string | undefined) ?? '—';

  // Re-run is only meaningful while both source layers still exist.
  const canRerun =
    !!topLayerId &&
    !!bottomLayerId &&
    candidateLayers.some((l) => l.id === topLayerId) &&
    candidateLayers.some((l) => l.id === bottomLayerId);

  const handleRerun = () => {
    if (!canRerun) return;
    const gate = featureCheck('analysis.surfaceDifference');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    openDialog(
      <RunSurfaceDifferenceDialog
        projectId={projectId}
        candidateLayers={candidateLayers}
        initialTopLayerId={topLayerId}
        initialBottomLayerId={bottomLayerId}
        initialValues={{
          name: (input.outputName as string | undefined) ?? job.label ?? '',
          topLayerId,
          bottomLayerId,
          topSurfaceIds: (input.topSurfaceIds as string[] | null) ?? [],
          bottomSurfaceIds: (input.bottomSurfaceIds as string[] | null) ?? [],
          topPcMethod: asPcMethod(input.topPcMethod),
          bottomPcMethod: asPcMethod(input.bottomPcMethod),
          resolution: (params.resolution as number) ?? 0.25,
          // Cut is the NEGATIVE side of `dz = top - bottom`, so the red end of
          // the baked ramp must be the low one (T4).
          colormap: (params.colormap as string) ?? defaultRampName('surfaceDifference'),
          fillHoles: (params.fillHoles as number) ?? 0,
          exportDxf: (params.exportDxf as boolean) ?? false,
        }}
      />,
    );
  };

  const timestampDate = job.completedAt ? new Date(job.completedAt) : new Date(job.createdAt);

  return (
    <div className='flex items-center gap-4 px-4 py-3 border-b last:border-b-0 border-slate-200/70 dark:border-stone-700/40'>
      <div className='flex items-center justify-center w-5 [&>svg]:size-5'>
        <StatusIcon status={job.status} />
      </div>
      <div className='flex-1 min-w-0'>
        <div className='text-sm font-medium truncate text-slate-900 dark:text-stone-100'>{job.label}</div>
        <div className='text-xs text-slate-600 dark:text-stone-400 truncate'>
          {topLayerName} − {bottomLayerName}
        </div>
      </div>
      <div className='text-xs text-slate-700 dark:text-stone-300 hidden md:block whitespace-nowrap'>
        {stats.cutVolumeM3 != null || stats.fillVolumeM3 != null
          ? `${t('dockPanel.cut')}: ${m3(stats.cutVolumeM3)} · ${t('dockPanel.fill')}: ${m3(stats.fillVolumeM3)}`
          : '—'}
      </div>
      <div className='text-xs text-slate-500 dark:text-stone-400 hidden lg:block whitespace-nowrap'>
        {formatDateTime(timestampDate)}
      </div>
      <Button variant='ghost' size='sm' onClick={handleRerun} disabled={!canRerun}>
        <RotateCw className='size-4' />
        <span className='hidden sm:inline ml-1'>{t('analyses.rerun')}</span>
      </Button>
    </div>
  );
}
